import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Download, Sparkles } from 'lucide-react';
import { aboutData, personalInfo } from '../data/mock';

const AboutPage = () => {
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (imageRef.current) {
        const scrolled = window.scrollY;
        imageRef.current.style.transform = `translateY(${scrolled * 0.1}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative">
      {/* Background */}
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-32 pb-20 relative overflow-hidden">
        {/* Floating shapes */}
        <div className="absolute top-1/4 right-10 w-72 h-72 bg-gradient-to-br from-[#00ff88]/10 to-transparent rounded-full blur-3xl morph-shape" />
        <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-gradient-to-br from-[#7c3aed]/10 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <div className="overflow-hidden mb-6">
                <span 
                  className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium"
                  style={{ animation: 'fadeUp 0.8s 0.1s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
                >
                  About Me
                </span>
              </div>

              <div className="overflow-hidden mb-8">
                <h1 
                  className="text-5xl lg:text-7xl font-bold text-white leading-tight"
                  style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(100%)' }}
                >
                  Hello, I'm{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">
                    {personalInfo.name}
                  </span>
                </h1>
              </div>

              <div 
                className="space-y-6 mb-10"
                style={{ animation: 'fadeUp 1s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
              >
                {aboutData.bio.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-white/50 text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div 
                className="flex flex-wrap gap-4"
                style={{ animation: 'fadeUp 1s 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
              >
                <Link to="/contact">
                  <button className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      Get in Touch
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <Link to="/cv">
                  <button className="group px-8 py-4 bg-white/5 border border-white/20 text-white rounded-full font-semibold text-lg hover:border-[#00ff88] hover:text-[#00ff88] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      <Download size={18} />
                      Download CV
                    </span>
                  </button>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div 
              className="relative"
              style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
            >
              <div ref={imageRef} className="relative">
                {/* Decorative frame */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#00ff88]/20 via-transparent to-[#7c3aed]/20 rounded-3xl blur-xl" />
                <div className="absolute -inset-px bg-gradient-to-br from-[#00ff88] to-[#7c3aed] rounded-3xl opacity-50" />
                
                <div className="relative rounded-3xl overflow-hidden">
                  <img
                    src={aboutData.image}
                    alt={personalInfo.name}
                    className="w-full aspect-[4/5] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-6 -left-6 glass-card px-6 py-4 float-animation">
                  <p className="text-[#00ff88] font-bold text-2xl">7+</p>
                  <p className="text-white/60 text-sm">Years Experience</p>
                </div>

                {/* Floating badge 2 */}
                <div className="absolute -top-6 -right-6 glass-card px-6 py-4 float-animation" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="text-[#00ff88]" size={20} />
                    <span className="text-white font-medium">Award Winner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00ff88]/5 via-transparent to-[#7c3aed]/5" />
        
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative z-10">
          <div className="reveal-on-scroll stagger-children">
            <div className="w-20 h-20 mx-auto mb-10 rounded-full bg-gradient-to-br from-[#00ff88]/20 to-[#7c3aed]/20 flex items-center justify-center">
              <span className="text-5xl text-[#00ff88]">“</span>
            </div>
            <blockquote className="text-3xl lg:text-5xl font-medium text-white leading-tight mb-8">
              {aboutData.philosophy}
            </blockquote>
            <cite className="text-white/40 text-xl not-italic">
              — {aboutData.philosophyAuthor}
            </cite>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="reveal-on-scroll stagger-children text-center mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-sm font-medium mb-4">
              My Journey
            </span>
            <h2 className="text-5xl lg:text-6xl font-bold text-white">
              Career Timeline
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00ff88] via-[#7c3aed] to-transparent" />

            <div className="space-y-16">
              {aboutData.milestones.map((milestone, index) => (
                <div
                  key={milestone.year}
                  className="reveal-on-scroll stagger-children relative"
                >
                  <div className={`flex items-center gap-8 lg:gap-16 ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Timeline dot */}
                    <div className="absolute left-8 lg:left-1/2 w-4 h-4 rounded-full bg-[#00ff88] transform -translate-x-1/2 ring-4 ring-black z-10">
                      <div className="absolute inset-0 rounded-full bg-[#00ff88] animate-ping opacity-50" />
                    </div>

                    {/* Content card */}
                    <div className={`flex-1 ml-20 lg:ml-0 ${index % 2 === 0 ? 'lg:pr-20' : 'lg:pl-20'}`}>
                      <div className="glass-card p-8 hover:-translate-y-2 transition-all duration-500">
                        <div className="flex items-center gap-3 mb-4">
                          <Calendar size={18} className="text-[#00ff88]" />
                          <span className="text-[#00ff88] font-bold text-lg">{milestone.year}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">{milestone.title}</h3>
                        <p className="text-white/50">{milestone.description}</p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="hidden lg:block flex-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Workspace */}
      <section className="py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal-on-scroll stagger-children">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
                My Space
              </span>
              <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
                Where Magic<br />Happens
              </h2>
              <p className="text-white/50 text-lg leading-relaxed mb-8">
                A clean, minimal workspace helps me focus on what matters most – creating exceptional user experiences.
              </p>
              <ul className="space-y-4">
                {[
                  'Dual monitor setup for design & development',
                  'Standing desk for better posture',
                  'Natural lighting for color accuracy',
                  'Noise-canceling environment for deep work'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4 text-white">
                    <div className="w-2 h-2 rounded-full bg-[#00ff88]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="reveal-on-scroll stagger-children">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src={aboutData.workspaceImage}
                  alt="Workspace"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal-on-scroll stagger-children glass-card p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Interested in Working Together?
              </h2>
              <p className="text-white/50 text-lg mb-10">
                I'm always open to discussing new projects and opportunities.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <button className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      Start a Conversation
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <Link to="/work">
                  <button className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-full font-semibold hover:border-[#00ff88] hover:text-[#00ff88] transition-all duration-300">
                    View My Work
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
